<template>
  <div class="node-card" :class="{ 'details-expanded': isDetailsExpanded, 'has-error': hasError, 'skipped': isSkipped, 'children-collapsed': isChildrenCollapsed, 'has-children': hasChildren, 'completed': isCompleted }" :style="hierarchyStyle">
    <div class="node-header" @click="onHeaderClick">
      <div class="node-header-left">
        <span v-if="hasChildren" class="collapse-icon" :title="isChildrenCollapsed ? 'Expand children' : 'Collapse children'">
          {{ isChildrenCollapsed ? '▶' : '▼' }}
        </span>
        <span v-else class="collapse-icon-spacer"></span>
        <StatusBadge :status="nodeState.status" size="small" />
        <span class="node-name">{{ node.name }}</span>
        <span class="node-type">{{ node.type }}</span>
        <span v-if="hasError && nodeState.error" class="error-badge" :title="nodeState.error">
          <i class="bi bi-exclamation-triangle"></i> Error
        </span>
        <span v-if="node.paused" class="paused-badge">PAUSED</span>
        <span v-if="depth > 0" class="depth-badge">Level {{ depth }}</span>
        <span v-if="hasChildren" class="children-badge">{{ childrenCount }} {{ childrenCount === 1 ? 'child' : 'children' }}</span>
      </div>
      <div class="node-header-right">
        <span v-if="duration" class="node-duration">{{ duration }}</span>
        
        <!-- Node control buttons (only for running workflows) -->
        <div v-if="showControls" class="node-controls" @click.stop>
          <button 
            v-if="!node.paused && canPauseNode"
            @click="$emit('pause-node', node.id)"
            class="btn-node-control pause"
            title="Pause Node"
          >
            <i class="bi bi-pause-circle"></i>
          </button>
          <button 
            v-if="node.paused"
            @click="$emit('resume-node', node.id)"
            class="btn-node-control resume"
            title="Resume Node"
          >
            <i class="bi bi-play-circle"></i>
          </button>
          <button 
            v-if="canKillNode"
            @click="$emit('kill-node', node.id)"
            class="btn-node-control kill"
            title="Kill Node"
          >
            <i class="bi bi-x-circle"></i>
          </button>
        </div>
        
        <!-- Eye icon to toggle details -->
        <button 
          class="btn-toggle-details"
          @click.stop="toggleDetails"
          :title="isDetailsExpanded ? 'Hide details' : 'Show details'"
        >
          <i :class="isDetailsExpanded ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>
    </div>
    
    <div v-if="isDetailsExpanded" class="node-body">
      <div class="node-info">
        <div class="info-row">
          <span class="info-label">Node ID:</span>
          <span class="info-value">{{ node.id }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Action:</span>
          <span class="info-value">{{ node.action }}</span>
        </div>
        <div v-if="nodeState.starttime || nodeState.start_time" class="info-row">
          <span class="info-label">Start Time:</span>
          <span class="info-value">{{ formatTimestamp(nodeState.starttime || nodeState.start_time) }}</span>
        </div>
        <div v-if="nodeState.endtime || nodeState.end_time" class="info-row">
          <span class="info-label">End Time:</span>
          <span class="info-value">{{ formatTimestamp(nodeState.endtime || nodeState.end_time) }}</span>
        </div>
        <div v-if="nodeState.retries > 0" class="info-row">
          <span class="info-label">Retries:</span>
          <span class="info-value">{{ nodeState.retries }}</span>
        </div>
      </div>
      
      <div v-if="nodeState.error" class="node-error">
        <h4>Error:</h4>
        <pre>{{ nodeState.error }}</pre>
      </div>
      
      <div v-if="hasInput" class="node-input">
        <h4>Input:</h4>
        <JsonViewer :value="nodeState.input" :expand-depth="1" copyable sort></JsonViewer>
      </div>
      
      <div v-if="hasResults" class="node-results">
        <h4>Results:</h4>
        <JsonViewer :value="nodeState.results" :expand-depth="1" copyable sort></JsonViewer>
      </div>
      
      <div v-if="node.data.expression" class="node-expression">
        <h4>Expression:</h4>
        <MonacoEditor 
          :value="node.data.expression" 
          :language="'javascript'" 
          :height="500"
          :readOnly="true"
          :showFullscreenButton="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import StatusBadge from './StatusBadge.vue';
import JsonViewer from 'vue-json-viewer';
import MonacoEditor from '@/components/form/Editor/MonacoEditor.vue';
import { formatTimestamp, formatDurationShort } from '@/shared/workflowHistoryUtils';

export default {
  name: 'NodeCard',
  components: {
    StatusBadge,
    JsonViewer,
    MonacoEditor
  },
  props: {
    node: {
      type: Object,
      required: true
    },
    nodeUniqueKey: {
      type: String,
      required: true
    },
    depth: {
      type: Number,
      default: 0
    },
    hasChildren: {
      type: Boolean,
      default: false
    },
    childrenCount: {
      type: Number,
      default: 0
    },
    showControls: {
      type: Boolean,
      default: false
    },
    workflowStatus: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isDetailsExpanded: false,
      isChildrenCollapsed: false
    };
  },
  computed: {
    nodeState() {
      // Normalize state - support both capitalized (State) and lowercase (state)
      return this.node.State || this.node.state || {};
    },
    duration() {
      const startTime = this.nodeState.starttime || this.nodeState.start_time;
      const endTime = this.nodeState.endtime || this.nodeState.end_time;
      
      if (startTime && endTime) {
        const start = new Date(startTime);
        const end = new Date(endTime);
        const durationMs = end - start;
        return formatDurationShort(durationMs);
      }
      return null;
    },
    hasError() {
      return this.nodeState.status === 'failed' || this.nodeState.status === 'errored' || !!this.nodeState.error;
    },
    isSkipped() {
      return this.nodeState.status === 'skipped';
    },
    isCompleted() {
      return this.nodeState.status === 'completed';
    },
    hierarchyStyle() {
      return {
        marginLeft: `${this.depth * 30}px`
      };
    },
    hasResults() {
      return this.nodeState.results && Object.keys(this.nodeState.results).length > 0;
    },
    hasInput() {
      return this.nodeState.input && Object.keys(this.nodeState.input).length > 0;
    },
    canPauseNode() {
      // Can pause if workflow is running and node is pending or running
      return this.workflowStatus === 'running' && 
             (this.nodeState.status === 'pending' || this.nodeState.status === 'running');
    },
    canKillNode() {
      // Can kill if workflow is running/paused and node is not completed
      return (this.workflowStatus === 'running' || this.workflowStatus === 'paused') && 
             this.nodeState.status !== 'completed';
    }
  },
  methods: {
    formatTimestamp,
    toggleDetails() {
      this.isDetailsExpanded = !this.isDetailsExpanded;
    },
    onHeaderClick() {
      if (this.hasChildren) {
        this.toggleChildren();
      }
    },
    toggleChildren() {
      if (!this.hasChildren) return;
      this.isChildrenCollapsed = !this.isChildrenCollapsed;
      this.$emit('toggle-children', this.nodeUniqueKey, this.isChildrenCollapsed);
    }
  }
};
</script>

<style scoped>
.node-card {
  border: 1px solid var(--border-default);
  border-radius: 8px;
  margin-bottom: 12px;
  background: var(--bg-card);
  transition: all 0.2s ease;
  position: relative;
}

.node-card:hover {
  border-color: var(--border-hover);
}

.node-card.has-error {
  border-color: #ef4444;
}

.node-card.skipped {
  border-color: #9ca3af;
  opacity: 0.7;
}

.node-card.completed {
  border-color: #10b981;
}

.node-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  user-select: none;
}

.node-card.has-children .node-header {
  cursor: pointer;
}

.node-card.has-children .node-header:hover {
  background-color: var(--form-bg);
}

.node-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.collapse-icon {
  font-size: 14px;
  color: var(--primary-start);
  width: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: bold;
  flex-shrink: 0;
}

.collapse-icon:hover {
  color: #8579ff;
  transform: scale(1.2);
}

.collapse-icon-spacer {
  width: 14px;
  flex-shrink: 0;
}

.node-name {
  font-weight: 600;
  color: var(--text-primary);
}

.node-type {
  padding: 2px 8px;
  background-color: var(--bg-card);
  border-radius: 4px;
  font-size: 12px;
  color: #9ca3af;
  border: 1px solid var(--border-default);
}

.node-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.paused-badge {
  padding: 2px 8px;
  background-color: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.error-badge {
  padding: 2px 8px;
  background-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(239, 68, 68, 0.3);
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-badge i {
  font-size: 12px;
}

.depth-badge {
  padding: 2px 8px;
  background-color: rgba(108, 99, 255, 0.2);
  color: var(--primary-start);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(108, 99, 255, 0.3);
}

.children-badge {
  padding: 2px 8px;
  background-color: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(156, 163, 175, 0.3);
}

.node-controls {
  display: flex;
  gap: 4px;
}

.btn-node-control {
  background: transparent;
  border: 1px solid #6b7280;
  color: #9ca3af;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-node-control:hover {
  background-color: rgba(107, 114, 128, 0.1);
  border-color: #9ca3af;
  color: var(--text-primary);
}

.btn-node-control.pause:hover {
  border-color: #f59e0b;
  color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.1);
}

.btn-node-control.resume:hover {
  border-color: #10b981;
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.1);
}

.btn-node-control.kill:hover {
  border-color: #ef4444;
  color: #ef4444;
  background-color: rgba(239, 68, 68, 0.1);
}

.btn-toggle-details {
  background: transparent;
  border: 1px solid var(--primary-start);
  color: var(--primary-start);
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
}

.btn-toggle-details:hover {
  background-color: rgba(108, 99, 255, 0.1);
  border-color: #8579ff;
  color: #8579ff;
}

.node-duration {
  font-size: 13px;
  color: #9ca3af;
}

.node-body {
  padding: 0 16px 16px 16px;
  border-top: 1px solid var(--border-default);
}

.node-info {
  margin-top: 12px;
}

.info-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.info-label {
  font-weight: 600;
  color: #9ca3af;
  min-width: 100px;
}

.info-value {
  color: var(--text-primary);
}

.node-error,
.node-input,
.node-results,
.node-expression {
  margin-top: 16px;
}

.node-error h4,
.node-input h4,
.node-results h4,
.node-expression h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.node-error pre {
  background-color: var(--form-bg);
  border: 1px solid #ef4444;
  color: #fca5a5;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 13px;
  margin: 0;
}

.node-expression {
  border: 1px solid var(--border-default);
  border-radius: 6px;
  overflow: hidden;
}

/* JSON Viewer dark theme styling */
.node-input .jv-container,
.node-results .jv-container {
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  padding: 12px;
}

.node-input .jv-container .jv-code,
.node-results .jv-container .jv-code {
  padding: 0;
}

.node-input >>> .jv-ellipsis,
.node-results >>> .jv-ellipsis {
  color: #9ca3af !important;
  background-color: var(--border-default) !important;
  padding: 2px 6px !important;
  border-radius: 3px !important;
  cursor: pointer !important;
}

.node-input >>> .jv-ellipsis:hover,
.node-results >>> .jv-ellipsis:hover {
  background-color: var(--bg-card-secondary) !important;
}

.node-input >>> .jv-button,
.node-results >>> .jv-button {
  color: #9ca3af !important;
  background-color: var(--border-default) !important;
  border-radius: 3px !important;
}

.node-input >>> .jv-button:hover,
.node-results >>> .jv-button:hover {
  background-color: var(--bg-card-secondary) !important;
}

.node-input >>> .jv-key,
.node-results >>> .jv-key {
  color: #60a5fa !important;
}

.node-input >>> .jv-item.jv-string,
.node-results >>> .jv-item.jv-string {
  color: #10b981 !important;
}

.node-input >>> .jv-item.jv-number,
.node-results >>> .jv-item.jv-number {
  color: #f59e0b !important;
}

.node-input >>> .jv-item.jv-boolean,
.node-results >>> .jv-item.jv-boolean {
  color: var(--primary-start) !important;
}

.node-input >>> .jv-item.jv-null,
.node-results >>> .jv-item.jv-null {
  color: #ef4444 !important;
}
</style>
